import { Fragment, useState } from "react";
import type { FacturaDeLote, LoteBanco, LotePago, LotePagoDetalle } from "../types/reviewItem";
import { formatFecha, formatMonto } from "../utils/formato";
import {
  CLASE_ESTADO_LOTE,
  ETIQUETA_ESTADO_LOTE,
  accionesDeLote,
  esFinal,
  lotesBancoParaVincular,
  lotesDestino,
  resumenConciliacion,
  textoAprobaciones,
  textoSugerenciaVinculo,
  type CandidatoVinculo,
  type VinculoImportacion,
} from "../utils/lotesPago";
import { DetalleLotePago } from "./DetalleLotePago";

export interface EstadoDetalleLote {
  id: number;
  lote: LotePagoDetalle | null;
  error: string | null;
}

/**
 * Panel "Lotes de pago" de Control de pagos. [NUEVO 04/11/2026]
 *
 * Solo se monta con la funcionalidad `lotes_pago` prendida. Las acciones que
 * modifican (vincular, corregir, eliminar, sacar o mover facturas) solo
 * aparecen con `esContaduria`; Control de pagos lo ve de solo lectura. Las
 * confirmaciones las hace quien pasa los callbacks, igual que en
 * LotesBancoPanel.
 */
export function LotesPagoPanel({
  lotes,
  error,
  onReintentar,
  esContaduria,
  lotesBanco,
  ocupado,
  detalle,
  onVerDetalle,
  onVincular,
  onCorregir,
  onEliminar,
  onQuitarFactura,
  onMoverFactura,
}: {
  lotes: LotePago[];
  /** Si no se pudieron cargar los lotes: la bandeja sigue funcionando igual. */
  error: string | null;
  onReintentar: () => void;
  esContaduria: boolean;
  /** Lotes del banco ya cargados, para ofrecer cuál vincular. */
  lotesBanco: LoteBanco[];
  /** Mientras corre una acción sobre un lote. */
  ocupado: boolean;
  detalle: EstadoDetalleLote | null;
  /** Abre o cierra el detalle de ese lote. */
  onVerDetalle: (id: number) => void;
  /** Devuelve si quedó vinculado (false si se canceló la confirmación o falló). */
  onVincular: (lote: LotePago, loteBanco: LoteBanco) => Promise<boolean>;
  onCorregir: (lote: LotePago) => void;
  onEliminar: (lote: LotePago) => void;
  onQuitarFactura: (lote: LotePago, factura: FacturaDeLote) => void;
  onMoverFactura: (lote: LotePago, factura: FacturaDeLote, destino: LotePago) => void;
}) {
  const [verPagados, setVerPagados] = useState(false);
  const [bancoElegido, setBancoElegido] = useState<Record<number, string>>({});

  const pagados = lotes.filter((l) => esFinal(l.estado));
  const visibles = verPagados ? lotes : lotes.filter((l) => !esFinal(l.estado));

  return (
    <section className="panel-lotes-banco panel-lotes-pago">
      <h2>Lotes de pago</h2>
      <p className="subtitle">
        Agrupá facturas habilitadas en un lote, vinculalo al PDF del banco y queda listo para que lo aprueben.
      </p>

      {error ? (
        <p className="error-text">
          {error}{" "}
          <button className="btn-ghost" onClick={onReintentar}>
            Reintentar
          </button>
        </p>
      ) : null}

      {pagados.length > 0 && (
        <button className="btn-ghost" aria-pressed={verPagados} onClick={() => setVerPagados((v) => !v)}>
          {verPagados ? "Ocultar los pagados" : `Ver también los pagados (${pagados.length})`}
        </button>
      )}

      {visibles.length === 0 && !error ? (
        <p className="sin-resolver">
          No hay lotes de pago en curso.
          {esContaduria ? " Seleccioná facturas habilitadas en la tabla y tocá «Crear lote»." : ""}
        </p>
      ) : null}

      {visibles.length > 0 && (
        <table className="compact lotes-pago">
          <thead>
            <tr>
              <th>Lote</th>
              <th className="celda-monto">Total</th>
              <th className="celda-monto">Facturas / proveedores</th>
              <th>Estado</th>
              <th>Conciliación con el banco</th>
              <th>Aprobaciones</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((l) => {
              const acc = accionesDeLote(l, esContaduria);
              const opcionesBanco = acc.vincular ? lotesBancoParaVincular(lotesBanco, l.moneda) : [];
              const elegido = bancoElegido[l.id] ?? "";
              const loteBancoElegido = opcionesBanco.find((b) => String(b.id) === elegido);
              const abierto = detalle !== null && detalle.id === l.id;
              const noCierra = l.cierra !== null && l.cierra !== undefined && !Boolean(l.cierra);
              const destinos = lotesDestino(lotes, l.moneda, l.id);
              return (
                <Fragment key={l.id}>
                  <tr>
                    <td className="wrap-ok">
                      <b>{l.nombre}</b>
                      <span className="sub-dato">Armó {l.creado_por.split("@")[0]}</span>
                    </td>
                    <td className="mono celda-monto">
                      {formatMonto(l.total)}
                      <span className="moneda">{l.moneda}</span>
                    </td>
                    <td className="mono celda-monto">
                      {l.cantidad_facturas} / {l.cantidad_proveedores}
                    </td>
                    <td>
                      <span className={`badge ${CLASE_ESTADO_LOTE[l.estado]}`}>{ETIQUETA_ESTADO_LOTE[l.estado]}</span>
                      {l.motivo_rechazo ? (
                        <span className="sub-dato rejection-reason" title={l.motivo_rechazo}>
                          Motivo: {l.motivo_rechazo}
                        </span>
                      ) : null}
                    </td>
                    <td className="wrap-ok">
                      <span className={noCierra ? "error-text" : undefined}>{resumenConciliacion(l)}</span>
                      <span className="sub-dato">
                        PDF del banco: {l.lote_banco_id_transaccion ?? "sin vincular"}
                      </span>
                    </td>
                    <td className="wrap-ok">{textoAprobaciones(l.aprobaciones)}</td>
                    <td className="actions-cell">
                      <button className="btn-ghost" aria-expanded={abierto} onClick={() => onVerDetalle(l.id)}>
                        {abierto ? "Ocultar detalle" : "Ver detalle"}
                      </button>
                      {acc.vincular && opcionesBanco.length > 0 && (
                        <span className="vincular-lote">
                          <select
                            aria-label={`PDF del banco para vincular a ${l.nombre}`}
                            value={elegido}
                            disabled={ocupado}
                            onChange={(e) => setBancoElegido((prev) => ({ ...prev, [l.id]: e.target.value }))}
                          >
                            <option value="">Vincular PDF del banco…</option>
                            {opcionesBanco.map((b) => (
                              <option key={b.id} value={String(b.id)}>
                                {`${formatFecha(b.fecha)} · ${b.moneda} ${formatMonto(b.importe_total)} · ID ${b.id_transaccion}`}
                              </option>
                            ))}
                          </select>
                          <button
                            className="btn-secundario"
                            disabled={ocupado || !loteBancoElegido}
                            onClick={async () => {
                              if (!loteBancoElegido) return;
                              // Solo se limpia si quedó vinculado: al cancelar, la elección queda.
                              if (await onVincular(l, loteBancoElegido)) {
                                setBancoElegido((prev) => ({ ...prev, [l.id]: "" }));
                              }
                            }}
                          >
                            Vincular
                          </button>
                        </span>
                      )}
                      {acc.vincular && opcionesBanco.length === 0 && (
                        <span className="sin-resolver" title="Importá el PDF del banco para poder vincularlo">
                          Sin PDF del banco para vincular
                        </span>
                      )}
                      {acc.corregir && (
                        <button className="btn-ghost" disabled={ocupado} onClick={() => onCorregir(l)}>
                          Corregir
                        </button>
                      )}
                      {acc.eliminar && (
                        <button className="btn-ghost btn-quitar-lote" disabled={ocupado} onClick={() => onEliminar(l)}>
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                  {abierto && detalle && (
                    <tr className="detalle-row">
                      <td colSpan={7}>
                        {detalle.error ? <p className="error-text">{detalle.error}</p> : null}
                        {!detalle.error && detalle.lote === null ? <p className="subtitle">Cargando detalle...</p> : null}
                        {detalle.lote ? (
                          <DetalleLotePago
                            lote={detalle.lote}
                            accionesFactura={
                              acc.editarFacturas
                                ? (f) => (
                                    <>
                                      <button
                                        className="btn-ghost"
                                        disabled={ocupado}
                                        onClick={() => onQuitarFactura(l, f)}
                                      >
                                        Sacar del lote
                                      </button>
                                      {destinos.length > 0 && (
                                        <select
                                          aria-label="Mover la factura a otro lote"
                                          value=""
                                          disabled={ocupado}
                                          onChange={(e) => {
                                            const destino = destinos.find((d) => String(d.id) === e.target.value);
                                            if (destino) onMoverFactura(l, f, destino);
                                          }}
                                        >
                                          <option value="">Mover a…</option>
                                          {destinos.map((d) => (
                                            <option key={d.id} value={String(d.id)}>
                                              {d.nombre}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                    </>
                                  )
                                : undefined
                            }
                          />
                        ) : null}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

/**
 * Línea extra del resumen de importación de PDFs del banco: vincular el PDF
 * recién importado a un lote de la app. [NUEVO 04/11/2026]
 *
 * Nunca vincula sola: aunque haya una única coincidencia, hace falta el clic
 * (si hay dos lotes en pesos el mismo día, una sugerencia equivocada no tiene
 * que quedar registrada).
 */
export function VincularDesdeImportacion({
  vinculo,
  monedaDe,
  puedeVincular,
  ocupado,
  onVincular,
}: {
  vinculo: VinculoImportacion;
  /** Moneda del lote de la app (formato bandeja), si se conoce. */
  monedaDe: (loteId: number) => string | undefined;
  puedeVincular: boolean;
  ocupado: boolean;
  onVincular: (candidato: CandidatoVinculo) => void;
}) {
  const [elegido, setElegido] = useState("");

  if (vinculo.vinculado) {
    return (
      <span className="vinculo-importacion">
        Vinculado a {vinculo.vinculado.nombre}{" "}
        <span className={`badge ${CLASE_ESTADO_LOTE[vinculo.vinculado.estado]}`}>
          {ETIQUETA_ESTADO_LOTE[vinculo.vinculado.estado]}
        </span>
      </span>
    );
  }
  if (!puedeVincular) return null;
  if (vinculo.sugerido) {
    const sugerido = vinculo.sugerido;
    return (
      <span className="vinculo-importacion">
        {textoSugerenciaVinculo(sugerido, monedaDe(sugerido.id))}{" "}
        <button className="btn-secundario" disabled={ocupado} onClick={() => onVincular(sugerido)}>
          Vincular
        </button>
      </span>
    );
  }
  if (vinculo.candidatos.length === 0) {
    return <span className="vinculo-importacion sin-resolver">No hay lotes de pago abiertos en esta moneda para vincular.</span>;
  }
  const candidato = vinculo.candidatos.find((c) => String(c.id) === elegido);
  return (
    <span className="vinculo-importacion">
      <select
        aria-label="Lote de pago para vincular este PDF"
        value={elegido}
        disabled={ocupado}
        onChange={(e) => setElegido(e.target.value)}
      >
        <option value="">Ningún lote coincide exacto: elegí uno…</option>
        {vinculo.candidatos.map((c) => {
          const moneda = monedaDe(c.id);
          return (
            <option key={c.id} value={String(c.id)}>
              {`${c.nombre} (${moneda ? `${moneda} ` : ""}${formatMonto(c.total)})`}
            </option>
          );
        })}
      </select>{" "}
      <button className="btn-secundario" disabled={ocupado || !candidato} onClick={() => candidato && onVincular(candidato)}>
        Vincular
      </button>
    </span>
  );
}
